import { Injectable } from '@angular/core';
import { StoreService } from '@zaoblako/web/core/services/store.service';
import { TypeFormEnum } from '@zaoblako/web/shared/components/form-sidebar/enums/type.form.enum';
import {
  FormSizeType,
  FormState,
  FormType,
  FormZIndexType
} from '@zaoblako/web/shared/components/form-sidebar/models/form-state';
import {
  formInitialState,
  formsInitialState
} from '@zaoblako/web/shared/components/form-sidebar/state/form-initial-state';
import { BehaviorSubject, Observable, Subject } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class FormStoreService extends StoreService<FormState> {
  protected state = new BehaviorSubject(formsInitialState);

  public get getState() {
    return this.state.value;
  }

  public constructor() {
    super();
  }

  public saveNext<T = any>(entity: T, index: number) {
    this.getState.forms[index]?.onSave$.next(entity);
    this.closeSidebar(index);
  }

  public deleteNext(index: number) {
    this.getState.forms[index]?.onDelete$.next();
    this.closeSidebar(index);
  }

  public addForm<T = any>(
    type: TypeFormEnum,
    options: {
      headerText?: string;
      widthSize?: FormSizeType;
      isNotShowRedirectInCreate?: boolean;
      typeZIndex?: FormZIndexType;
      data?: any;
    }
  ): { onSave: Observable<T>; onDelete: Observable<void> } {
    const updatedState: FormType = {
      ...formInitialState,
      isShowSidebar: true,
      onSave$: new Subject(),
      onDelete$: new Subject(),
      type
    };
    if (options?.headerText) {
      updatedState.headerText = options.headerText;
    }
    if (options?.widthSize) {
      updatedState.widthSize = options.widthSize;
    }
    if (options?.typeZIndex) {
      updatedState.typeZIndex = options.typeZIndex;
    }
    if (options?.isNotShowRedirectInCreate) {
      updatedState.isShowRedirectInCreate = false;
      updatedState.isRedirectInCreate = false;
    }
    if (options?.data) {
      updatedState.data = options.data;
    }

    this.updateState((state) => ({
      forms: [...state.forms, updatedState]
    }));
    return { onSave: updatedState.onSave$.asObservable(), onDelete: updatedState.onDelete$.asObservable() };
  }

  public editForm<T = any>(
    type: TypeFormEnum,
    entityId: number,
    options: {
      headerText?: string;
      widthSize?: FormSizeType;
      isNotShowDelete?: boolean;
      isShowShare?: boolean;
      typeZIndex?: FormZIndexType;
      data?: any;
    }
  ): { onSave: Observable<T>; onDelete: Observable<void> } {
    const updatedState: FormType = {
      ...formInitialState,
      isShowSidebar: true,
      loading: true,
      isEdit: true,
      isShowDelete: true,
      onSave$: new Subject(),
      onDelete$: new Subject(),
      entityId,
      type
    };
    if (options?.headerText) {
      updatedState.headerText = options.headerText;
    }
    if (options?.widthSize) {
      updatedState.widthSize = options.widthSize;
    }
    if (options?.typeZIndex) {
      updatedState.typeZIndex = options.typeZIndex;
    }
    if (options?.isNotShowDelete) {
      updatedState.isShowDelete = false;
    }
    if (options?.isShowShare) {
      updatedState.isShowShare = true;
    }
    if (options?.data) {
      updatedState.data = options.data;
    }

    this.updateState((state) => ({
      forms: [...state.forms, updatedState]
    }));
    return { onSave: updatedState.onSave$.asObservable(), onDelete: updatedState.onDelete$.asObservable() };
  }

  public setLoading(loading: boolean, index: number) {
    const forms = [...this.getState.forms];
    if (forms[index]) {
      forms[index].loading = loading;
    }
    this.updateState({
      forms
    });
  }

  public closeSidebar(index: number) {
    const forms = [...this.getState.forms];
    forms[index].onSave$.unsubscribe();
    forms[index].onDelete$.unsubscribe();
    forms.splice(index, 1);
    this.updateState({
      forms
    });
  }

  public toggleIsRedirectInCreate(value: boolean, index: number) {
    const forms = [...this.getState.forms];
    forms[index].isRedirectInCreate = value;
    this.updateState({
      forms
    });
  }
}
